// The capture seen through a receive filter: what a cavity of a given center
// frequency and bandwidth would have left of the band.
//
// The response is Butterworth, so the bandwidth control is the -3 dB width,
// which is the number a cavity is specified by. Two details keep it from being
// a plain gate. Attenuation stops at the filter's ultimate rejection, because a
// real cavity bottoms out. And a masked reading never falls below that bin's
// own noise floor, because a filter removes signal and not the receiver's own
// noise: a stopband running to -80 dB would be a picture of no radio at all,
// and the question the mask exists to answer is whether a signal would still
// show through, which only has an answer if the floor is still there.

import { dbToCenti, type Run } from '../model.ts'
import { colToHz, displayedBand, xToCol, type Clip, type View } from '../render/layout.ts'

export interface FilterSettings {
  filterOn: boolean
  /** null = the middle of the loaded band. */
  filterCenterHz: number | null
  /** null = half the loaded band. */
  filterBwHz: number | null
  /** Butterworth order: 1 is a lazy skirt, 8 is close to a brick wall. */
  filterOrder: number
  /** Ultimate stopband rejection, in dB below the passband. */
  filterRejectionDb: number
}

/** Skirt steepness offered in the panel; a two-cavity 33 cm filter sits near 3. */
export const FILTER_ORDERS = [1, 2, 3, 4, 6, 8] as const

export const MIN_REJECTION_DB = 3
export const MAX_REJECTION_DB = 120

/**
 * Bandwidths the slider offers, in whole MHz from 3 to 30.
 *
 * Fixed rather than derived from the loaded band, because it is a catalogue of
 * parts and not a property of the capture: a cavity for this band comes in a
 * handful of widths, and a slider that could land on 12.582 MHz was offering
 * a precision nothing downstream has.
 */
export const MIN_FILTER_BW_HZ = 3e6
export const MAX_FILTER_BW_HZ = 30e6
export const FILTER_BW_STEP_HZ = 1e6

/**
 * Attenuation in dB at `hz`: zero at the center, 3 at the passband edges, and
 * rising by `20 · order` dB per decade of detuning until it reaches
 * `rejectionDb` and stops.
 */
export function attenuationDb(
  hz: number,
  centerHz: number,
  bwHz: number,
  order: number,
  rejectionDb: number,
): number {
  if (!(bwHz > 0) || !(order > 0)) return 0
  const x = (hz - centerHz) / (bwHz / 2)
  // 10·log10(1 + x^2n), the Butterworth magnitude. At |x| = 1 this is 3.01 dB,
  // which is what makes the bandwidth setting the -3 dB width. Far out it
  // overflows to Infinity, and the cap is what the answer would have been.
  const db = 10 * Math.log10(1 + (x * x) ** order)
  return db > rejectionDb ? rejectionDb : db
}

/** A filter resolved against a loaded run: defaults filled in, clamped to the band. */
export interface ResolvedFilter {
  centerHz: number
  bwHz: number
  order: number
  rejectionDb: number
  /** The -3 dB edges, for drawing. */
  lowHz: number
  highHz: number
}

/** A bandwidth as the slider can express it: a whole number of MHz, 3 to 30. */
export function clampFilterBwHz(hz: number): number {
  if (!Number.isFinite(hz)) return MIN_FILTER_BW_HZ
  const whole = Math.round(hz / FILTER_BW_STEP_HZ) * FILTER_BW_STEP_HZ
  return clamp(whole, MIN_FILTER_BW_HZ, MAX_FILTER_BW_HZ)
}

/**
 * The filter the sliders mean against this run.
 *
 * The center is clamped to the band for the reason the frequency crop is:
 * settings persist across files, so a center typed for a 33 cm capture would
 * otherwise sit entirely outside a 2 m one and blank the plot with nothing on
 * screen to say why. The bandwidth is clamped to its own fixed range instead,
 * since which cavities exist does not depend on what was recorded. A null is
 * not a stale value but an unset one, and takes its default from what is
 * loaded.
 */
export function resolveFilter(run: Run, offsetHz: number, settings: FilterSettings): ResolvedFilter {
  const band = displayedBand(run, offsetHz)
  const span = band.highHz - band.lowHz
  const centerHz = clamp(settings.filterCenterHz ?? (band.lowHz + band.highHz) / 2, band.lowHz, band.highHz)
  const bwHz = clampFilterBwHz(settings.filterBwHz ?? span / 2)
  const order = settings.filterOrder > 0 ? settings.filterOrder : 3
  const rejectionDb = clamp(settings.filterRejectionDb, MIN_REJECTION_DB, MAX_REJECTION_DB)
  return { centerHz, bwHz, order, rejectionDb, lowHz: centerHz - bwHz / 2, highHz: centerHz + bwHz / 2 }
}

function clamp(value: number, lo: number, hi: number): number {
  if (!Number.isFinite(value)) return lo
  return value < lo ? lo : value > hi ? hi : value
}

/**
 * The mask the painter applies, sampled once per output pixel column: how much
 * to take off, and how far down it is allowed to go.
 */
export interface HeatMask {
  /** Centi-dB to subtract from each pixel column. */
  attenCenti: Int16Array
  /** Centi-dB that column cannot fall below: the bin's own noise floor. */
  floorCenti: Int16Array
}

export function buildHeatMask(clip: Clip, view: View, widthPx: number, filter: ResolvedFilter): HeatMask {
  const attenCenti = new Int16Array(widthPx)
  const floorCenti = new Int16Array(widthPx)
  const { floorDb, binCount, minDb } = clip.run
  // A bin that never had a reading has no floor of its own; the run's own
  // minimum is the quietest thing this receiver was seen to produce.
  const fallback = dbToCenti(minDb)
  for (let px = 0; px < widthPx; px++) {
    const col = xToCol(view, px + 0.5, widthPx)
    const hz = colToHz(clip, col)
    attenCenti[px] = dbToCenti(attenuationDb(hz, filter.centerHz, filter.bwHz, filter.order, filter.rejectionDb))
    const bin = Math.min(binCount - 1, Math.max(0, Math.round(col)))
    const db = floorDb[bin]!
    floorCenti[px] = Number.isNaN(db) ? fallback : dbToCenti(db)
  }
  return { attenCenti, floorCenti }
}

/**
 * What our own transmission of `widthHz` centered at `centerHz` would lose to
 * the filter, in dB.
 *
 * The attenuation at whichever edge of the footprint is further from the
 * filter's center, not the attenuation at the middle of it. A signal half-way
 * down the skirt is not a signal that lost the average of its two edges.
 */
export function insertionLossDb(centerHz: number, widthHz: number, filter: ResolvedFilter): number {
  const at = (hz: number) => attenuationDb(hz, filter.centerHz, filter.bwHz, filter.order, filter.rejectionDb)
  const low = at(centerHz - widthHz / 2)
  const high = at(centerHz + widthHz / 2)
  return low > high ? low : high
}

/** The detuning at which the response is `db` down; Infinity once past the rejection cap. */
function detuneForDb(filter: ResolvedFilter, db: number): number {
  if (db >= filter.rejectionDb) return Infinity
  // Inverting 10·log10(1 + x^2n) = db for x, then scaling by the half-width.
  const x = (10 ** (db / 10) - 1) ** (1 / (2 * filter.order))
  return (filter.bwHz / 2) * x
}

/**
 * The centers at which a transmission of `widthHz` keeps its insertion loss
 * within `budgetDb`, as one span, or null when none do.
 *
 * One span rather than a set: the loss is the attenuation at the further edge
 * of the footprint, and that is monotone in how far the center is detuned, so
 * the qualifying centers are always contiguous around the filter's own center.
 */
export function budgetSpanHz(
  filter: ResolvedFilter,
  widthHz: number,
  budgetDb: number,
): { lowHz: number; highHz: number } | null {
  const half = detuneForDb(filter, budgetDb) - widthHz / 2
  if (!(half >= 0)) return null
  return { lowHz: filter.centerHz - half, highHz: filter.centerHz + half }
}

/** Attenuation in centi-dB at every bin of the clip's crop, indexed from `clip.bin0`. */
export function binAttenuationCenti(clip: Clip, filter: ResolvedFilter): Int16Array {
  const cols = clip.bin1 - clip.bin0
  const out = new Int16Array(cols)
  for (let c = 0; c < cols; c++) {
    const hz = clip.run.binHz[clip.bin0 + c]! + clip.offsetHz
    out[c] = dbToCenti(attenuationDb(hz, filter.centerHz, filter.bwHz, filter.order, filter.rejectionDb))
  }
  return out
}

/** One reading through the filter, for the tooltip. Matches what the painter does. */
export function maskedDb(clip: Clip, bin: number, db: number, filter: ResolvedFilter): number {
  const hz = colToHz(clip, bin)
  const floorDb = clip.run.floorDb[Math.min(clip.run.binCount - 1, Math.max(0, bin))]!
  const floor = Number.isNaN(floorDb) ? clip.run.minDb : floorDb
  const out = db - attenuationDb(hz, filter.centerHz, filter.bwHz, filter.order, filter.rejectionDb)
  if (out >= floor) return out
  return db < floor ? db : floor
}
